import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  MessageSquareTextIcon,
  FilesIcon,
  VideoIcon,
  SettingsIcon,
  LinkIcon,
  TicketIcon,
  CodeIcon,
  BookTextIcon,
  BrainIcon,
  TagIcon,
  CableIcon,
  ChevronDownIcon,
  LogOutIcon,
  MenuIcon,
  UserIcon,
  WandIcon,
  XIcon,
} from './icons.js';
import type { UserRole } from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { usePersistedState } from '../hooks/usePersistedState.js';

/**
 * App shell for authenticated routes — OneView (Galaxy) layout.
 *
 *   ┌──────────┬──────────────────────────────────────────────────────┐
 *   │ Sidebar  │ Topbar (breadcrumb · ⌘K search · user pill)          │
 *   │ pill-nav ├──────────────────────────────────────────────────────┤
 *   │          │ (optional Recent Conversations rail on /agent)       │
 *   │          │ Content                                              │
 *   └──────────┴──────────────────────────────────────────────────────┘
 *
 * The Recent Conversations rail itself lives on the KnowledgeAgent page —
 * it's rendered next to the agent content inside `main`, not by this shell.
 * Layout only provides the chrome (sidebar + topbar). The `kb_admin`
 * role identifier is retained at the type level (wire contract); UI labels
 * are OneNebula-specific.
 */
type NavGroupId = 'my-workspace' | 'workspace' | 'sources' | 'taxonomy' | 'admin';

interface NavItem {
  to: string;
  label: string;
  description: string;
  icon: typeof FilesIcon;
  group: NavGroupId;
  allowed?: UserRole[];
  /** Server-side feature flag (from /api/me flags) gating this entry. */
  flag?: 'canvasEnabled' | 'myAgentEnabled';
}

interface NavGroup {
  id: NavGroupId;
  label: string;
}

const NAV_GROUPS: NavGroup[] = [
  { id: 'my-workspace', label: 'My Workspace' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'sources', label: 'Sources' },
  { id: 'taxonomy', label: 'Taxonomy' },
  { id: 'admin', label: 'Admin' },
];

const NAV: NavItem[] = [
  {
    to: '/agent',
    label: 'Knowledge Agent',
    description: 'Ask a question',
    icon: MessageSquareTextIcon,
    group: 'workspace',
  },
  {
    to: '/my-agent',
    label: 'My Agent',
    description: 'Your personal assistant — your mail, calendar, chats and tickets',
    icon: BrainIcon,
    group: 'my-workspace',
    flag: 'myAgentEnabled',
  },
  {
    to: '/documents',
    label: 'Documents',
    description: 'Browse the knowledgebase',
    icon: FilesIcon,
    group: 'workspace',
  },
  {
    to: '/videos',
    label: 'Training Videos',
    description: 'Watch internal training; ask grounded questions',
    icon: VideoIcon,
    group: 'workspace',
  },
  {
    to: '/workspace/extractor',
    label: 'Data Extractor',
    description: 'Extract structured rows from PDFs and spreadsheets',
    icon: WandIcon,
    group: 'workspace',
  },
  {
    to: '/help',
    label: 'Help & Guides',
    description: 'How to use the agent, exports, extractor & templates',
    icon: BookTextIcon,
    group: 'workspace',
  },
  // ── Sources: where content comes from ───────────────────────────
  {
    to: '/admin/documents',
    label: 'Documents',
    description: 'Manage uploaded PDFs + the rest of the document corpus',
    icon: FilesIcon,
    group: 'sources',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/videos',
    label: 'Video Uploads',
    description: 'Upload and index training recordings',
    icon: VideoIcon,
    group: 'sources',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/confluence',
    label: 'Confluence Sources',
    description: 'Sync Confluence pages',
    icon: LinkIcon,
    group: 'sources',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/jira-sources',
    label: 'Jira Sources',
    description: 'Ingest Jira projects',
    icon: TicketIcon,
    group: 'sources',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/code-sources',
    label: 'Code Sources',
    description: 'Sync repos via aiagents.1nebula.com',
    icon: CodeIcon,
    group: 'sources',
    allowed: ['kb_admin'],
  },
  // ── Taxonomy: how content is classified + curated ───────────────
  {
    to: '/admin/specialisations',
    label: 'Specialisations',
    description: 'Manage the topic taxonomy applied to documents, Confluence and code',
    icon: TagIcon,
    group: 'taxonomy',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/document-types',
    label: 'Document Types',
    description: 'Inspect the document-type taxonomy and re-tag stale rows',
    icon: FilesIcon,
    group: 'taxonomy',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/doc-templates',
    label: 'Document Templates',
    description: 'Letterheads, proposals & legal templates for branded Word/PowerPoint export',
    icon: FilesIcon,
    group: 'taxonomy',
    allowed: ['kb_admin'],
  },
  {
    to: '/admin/canonical-facts',
    label: 'Canonical Facts',
    description: 'Authoritative overrides for stale corpus content',
    icon: BookTextIcon,
    group: 'taxonomy',
    allowed: ['kb_admin'],
  },
  // ── Admin: platform / integration config ────────────────────────
  {
    to: '/admin/extractor-templates',
    label: 'Extractor Templates',
    description: 'Browse extractor templates (admins can also create + edit)',
    icon: WandIcon,
    group: 'admin',
  },
  {
    to: '/admin/api-keys',
    label: 'API Keys',
    description: 'Issue + revoke keys for external integrations (AG-UI, MCP)',
    icon: SettingsIcon,
    group: 'admin',
    allowed: ['kb_admin'],
  },
];

function tenantLabel(tenantId: string): string {
  if (tenantId === 'onenebula') return 'OneNebula';
  if (tenantId === '_platform') return 'Platform';
  return tenantId.toUpperCase();
}

function roleLabel(role: UserRole): string {
  switch (role) {
    case 'kb_admin':
      return 'Admin';
    case 'sensitive_user':
      return 'Sensitive';
    case 'confidential_user':
      return 'Confidential';
    case 'exco_user':
      return 'Exco';
    case 'kb_user':
      return 'Employee';
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return (parts[0][0] ?? '?').toUpperCase();
  return ((parts[0][0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

/**
 * Derive a breadcrumb trail from the current pathname using the NAV[]
 * registry. Falls back to a single "Knowledgebase" crumb when nothing
 * matches. Crumbs are rendered with chevron separators in the topbar.
 */
function breadcrumbFor(pathname: string): string[] {
  // Routes outside NAV[] (e.g. /profile) need explicit crumbs so the topbar
  // doesn't fall back to the generic "Knowledgebase" label.
  if (pathname.startsWith('/profile')) return ['Profile'];
  if (pathname.startsWith('/settings/connections')) return ['Settings', 'Connections'];
  const item = NAV.find((i) => pathname.startsWith(i.to));
  if (!item) return ['Knowledgebase'];
  if (item.to.startsWith('/admin/')) return ['Admin', item.label.replace(/^Knowledgebase\s+/, '')];
  return [item.label];
}

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { user, flags, signOut } = useAuth();
  const location = useLocation();

  if (!user) return <></>;

  const items = NAV.filter(
    (i) => (!i.allowed || i.allowed.includes(user.role)) && (!i.flag || flags[i.flag]),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // Desktop sidebar collapse (separate from `navOpen` which is the mobile
  // drawer). Persisted across sessions so once a user collapses, it stays.
  const [navCollapsed, setNavCollapsed] = usePersistedState<boolean>(
    'kb.layout.navCollapsed',
    false,
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  const crumbs = breadcrumbFor(location.pathname);

  // Close mobile drawer on route change so navigating doesn't leave it open.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <div className="flex h-screen bg-nebula-app text-nebula-fg-1">
      {/* Mobile drawer scrim. z-[45] so it sits ABOVE the chat-history rail
          (z-40, rendered inside `main`) — otherwise the rail bleeds over the
          open nav. Below the sidebar itself (z-50). Tapping closes the drawer. */}
      {navOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-[45] bg-nebula-fg-1/40 lg:hidden"
        />
      )}

      {/* Sidebar — fixed drawer on mobile, static column on lg+.
          Desktop collapse (navCollapsed) shrinks to a 56px icon-only rail
          so the user keeps single-click navigation while reclaiming screen
          space. Group labels + brand wordmark hide; nav-item labels hide;
          icons stay clickable with the route name as tooltip. */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex shrink-0 flex-col border-r border-nebula-border-soft bg-nebula-surface transition-[transform,width] duration-200 lg:static lg:translate-x-0 ${
          navCollapsed ? 'lg:w-14' : 'w-64'
        } ${navOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Mobile-only top strip with close button. */}
        <div className="flex items-center justify-between border-b border-nebula-border-soft px-3 py-2 lg:hidden">
          <span className="ov-meta-upper">Menu</span>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="rounded-md p-1.5 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Brand — icon rail collapses the wordmark + tagline to keep the
            64px-tall header from blocking nav items in 56px-wide rail mode. */}
        <div
          className={`border-b border-nebula-border-soft py-5 ${
            navCollapsed ? 'lg:px-2 lg:flex lg:justify-center' : 'px-5'
          }`}
        >
          <img
            src="/1Nebula-01.png"
            alt="OneNebula"
            className={navCollapsed ? 'h-8 w-8 object-contain lg:h-8 lg:w-auto' : 'h-8 w-auto'}
          />
          <div className={`ov-meta-upper mt-2 ${navCollapsed ? 'lg:hidden' : ''}`}>
            Knowledgebase
          </div>
        </div>

        {/* Pill nav — rail mode hides group labels + per-item labels, keeping
            icons clickable. `title` attr supplies the route name on hover so
            users don't lose discoverability. */}
        <nav
          className={`flex-1 overflow-y-auto py-4 thin-scroll ${navCollapsed ? 'lg:px-2' : 'px-3'}`}
        >
          {NAV_GROUPS.map((group) => {
            const groupItems = items.filter((i) => i.group === group.id);
            if (groupItems.length === 0) return null;
            return (
              <div key={group.id} className="mb-4 last:mb-0">
                <div className={`ov-meta-upper px-3 pb-2 ${navCollapsed ? 'lg:hidden' : ''}`}>
                  {group.label}
                </div>
                <div className="space-y-1">
                  {groupItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        title={navCollapsed ? item.label : undefined}
                        className={({ isActive }): string =>
                          [
                            'group flex items-center gap-3 rounded-full text-sm font-ui transition-colors',
                            navCollapsed
                              ? 'lg:justify-center lg:px-0 lg:py-2 px-3 py-2'
                              : 'px-3 py-2',
                            isActive
                              ? 'bg-nebula-primary text-white font-medium'
                              : 'text-nebula-fg-2 hover:bg-nebula-tint hover:text-nebula-fg-1',
                          ].join(' ')
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon
                              size={18}
                              fill={isActive}
                              className={isActive ? 'text-white' : 'text-nebula-fg-3'}
                            />
                            <span className={`truncate ${navCollapsed ? 'lg:hidden' : ''}`}>
                              {item.label}
                            </span>
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar — 56px tall. */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-nebula-border-soft bg-nebula-surface px-4 sm:px-6">
          {/* Mobile hamburger. */}
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            className="-ml-1 rounded-md p-2 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1 lg:hidden"
          >
            <MenuIcon size={20} />
          </button>

          {/* Desktop sidebar collapse toggle. Always visible on lg+. Icon
              flips based on state: chevron-left = expanded (click to
              collapse); chevron-right = collapsed (click to expand). */}
          <button
            type="button"
            onClick={() => setNavCollapsed((v) => !v)}
            aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            className="hidden lg:inline-flex -ml-1 rounded-md p-2 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
          >
            {navCollapsed ? (
              <MenuIcon size={20} />
            ) : (
              <ChevronDownIcon size={20} className="rotate-90" />
            )}
          </button>

          {/* Breadcrumb — hidden on mobile, hamburger replaces it. */}
          <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center gap-1.5 lg:flex">
            {crumbs.map((c, i) => (
              <span key={`${c}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-nebula-fg-4">/</span>}
                <span
                  className={`truncate text-sm font-ui ${
                    i === crumbs.length - 1 ? 'font-medium text-nebula-fg-1' : 'text-nebula-fg-3'
                  }`}
                >
                  {c}
                </span>
              </span>
            ))}
          </nav>

          {/* Top-bar ⌘K palette removed — kept the slot empty for now so we
              can decide what (if anything) to put here later. */}

          {/* User pill */}
          <div className="ml-auto flex items-center gap-3" ref={menuRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-2.5 rounded-full px-1.5 py-1 text-left transition-colors hover:bg-nebula-tint"
                title="User menu"
              >
                {user.photoDataUrl ? (
                  <img
                    src={user.photoDataUrl}
                    alt={user.displayName}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-nebula-primary text-xs font-semibold text-white">
                    {initials(user.displayName)}
                  </div>
                )}
                <div className="hidden leading-tight sm:block">
                  <div className="text-[13px] font-medium font-ui text-nebula-fg-1">
                    {user.displayName}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-nebula-fg-3">
                    <span className="inline-flex items-center rounded bg-nebula-gray-5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-nebula-fg-2">
                      {tenantLabel(user.tenantId)}
                    </span>
                    <span>{roleLabel(user.role)}</span>
                  </div>
                </div>
                <ChevronDownIcon
                  size={14}
                  className={`text-nebula-fg-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-xl border border-nebula-border-soft bg-nebula-surface shadow-card"
                  style={{ boxShadow: 'var(--ov-shadow-popover)' }}
                >
                  <div className="border-b border-nebula-border-soft px-4 py-3 sm:hidden">
                    <div className="text-[13px] font-medium font-ui text-nebula-fg-1">
                      {user.displayName}
                    </div>
                    <div className="text-[11px] text-nebula-fg-3">{roleLabel(user.role)}</div>
                  </div>
                  <NavLink
                    to="/profile"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-nebula-fg-1 hover:bg-nebula-tint"
                  >
                    <UserIcon size={15} />
                    <span>Profile</span>
                  </NavLink>
                  <NavLink
                    to="/settings/connections"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-nebula-fg-1 hover:bg-nebula-tint"
                  >
                    <CableIcon size={15} />
                    <span>Connections</span>
                  </NavLink>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      void signOut();
                    }}
                    className="flex w-full items-center gap-2.5 border-t border-nebula-border-soft px-4 py-2.5 text-left text-sm text-nebula-fg-1 hover:bg-nebula-tint"
                  >
                    <LogOutIcon size={15} />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto bg-nebula-app">{children}</main>
      </div>
    </div>
  );
}
